// API de poses personalizadas (Interfaz V2). Tabla `poses_personalizadas`
// (RLS por empresa); imágenes en el bucket privado `modelos`, path
// `<id_company>/poses_custom/<filename>` → signed URLs para mostrar. El
// id_company va PRIMERO porque la policy del bucket `modelos` exige que el
// path empiece con `<id_company>/`.
//
// Crear: el backend (atlas `/v1/models/process-pose`, o n8n
// `procesar_pose_subida` con flag OFF) genera el muñeco-en-pose y devuelve el
// b64; el front guarda imagen + fila y el caller refetchea. Borrar: el cliente
// borra las suyas (política RLS DELETE acotada) + remueve la imagen del storage.
//
// GRATIS desde ADR-2026-08-03-subir-pose-es-gratis (antes: 2 créditos por pose).
// OJO con el rollback: el path n8n del flag OFF **sigue cobrando 2**, porque el
// cobro vive dentro del workflow y no se toca desde acá.

import { supabase } from '../../lib/supabase';
import {
  PROCESAR_POSE_URL,
  GENERATE_AUTH_TOKEN,
  USE_ATLAS_BACKEND,
} from '../../config';
import { atlasBackendFetch } from '../../lib/atlasBackend';
import { normalizeImageForUpload, UnsupportedImageFormatError } from '../../utils/heicConvert';
import type { PoseV2 } from './posesApi';

const BUCKET = 'modelos';
const SIGNED_TTL = 3600;

// File → base64 sin el prefix `data:...,` (mismo helper que modelsApi).
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const idx = result.indexOf(',');
      resolve(idx >= 0 ? result.slice(idx + 1) : result);
    };
    reader.onerror = () => reject(new Error('No se pudo leer el archivo'));
    reader.readAsDataURL(file);
  });
}

// El backend puede devolver el b64 con o sin prefix `data:image/...;base64,`.
function stripB64Prefix(raw: string): string {
  const idx = raw.indexOf(',');
  return idx >= 0 ? raw.slice(idx + 1) : raw;
}

// base64 → Blob para subir al bucket.
function b64ToBlob(b64: string, type = 'image/png'): Blob {
  const bytes = atob(b64);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  return new Blob([arr], { type });
}

/** Nombre genérico de las poses propias: "Pose propia 1", "Pose propia 2"… */
export const nombrePosePropia = (n: number) => `Pose propia ${n}`;

// `'Mi pose'` era el placeholder viejo (todas idénticas): se trata como si no
// hubiera nombre para que también se renumeren.
const displayName = (nombre: string | null, n: number): string => {
  const limpio = (nombre || '').trim();
  if (!limpio || limpio === 'Mi pose') return nombrePosePropia(n);
  return limpio;
};

/** Trae las poses personalizadas de la empresa, con signed URL para mostrar. */
export async function fetchMyCustomPoses(idCompany: string): Promise<PoseV2[]> {
  const { data, error } = await supabase
    .from('poses_personalizadas')
    .select('id, nombre, filename, cantidad_personas')
    .eq('id_company', idCompany)
    .order('created_at', { ascending: false });
  if (error || !data) {
    if (error) console.error('[posesCustomApi] fetchMyCustomPoses error', error);
    return [];
  }
  const rows = data as Array<{
    id: string;
    nombre: string | null;
    filename: string;
    cantidad_personas: number | null;
  }>;
  const out: PoseV2[] = [];
  for (const [i, p] of rows.entries()) {
    const path = `${idCompany}/poses_custom/${p.filename}`;
    // DOS firmas: el original en `imageUrl` —es lo que viaja como referencia al chat y a
    // la generación, y lo que `leerFirma` sabe renovar— y la miniatura en `thumbUrl`,
    // sólo para dibujar en las grillas (las fotos que sube la gente son de cámara)
    // (ADR-2026-09-25-revision-greptile-la-miniatura-no-viaja-en-el-adjunto).
    const [{ data: signed }, { data: miniatura }] = await Promise.all([
      supabase.storage.from(BUCKET).createSignedUrl(path, SIGNED_TTL),
      supabase.storage.from(BUCKET).createSignedUrl(path, SIGNED_TTL, {
        transform: { width: 440, height: 782, resize: 'contain', quality: 75 },
      }),
    ]);
    out.push({
      id: String(p.id),
      // Las filas viejas se guardaron con `nombre = null` y mostraban el
      // literal "Mi pose", igual para todas. El fallback numera por orden de
      // creación (la query viene DESC, por eso el índice se da vuelta), así
      // las que ya existen quedan homogéneas sin migrar nada. Las nuevas ya
      // traen el nombre guardado y ese gana.
      nombre: displayName(p.nombre, rows.length - i),
      filename: p.filename,
      categoria: null,
      imageUrl: signed?.signedUrl || '',
      ...(miniatura?.signedUrl ? { thumbUrl: miniatura.signedUrl } : {}),
      areasPrincipales: [],
      cantidadPersonas: p.cantidad_personas ?? 1,
      isCustom: true,
    });
  }
  return out;
}

/**
 * Sube una pose en 2 pasos:
 *  1. Genera el muñeco-en-pose (gratis; devuelve b64
 *     `image_pose`). Flag atlas ON → se sube la foto FUENTE al bucket
 *     `modelos` (`<id_company>/poses_custom/src_<uuid>`) y se manda la KEY a
 *     `/v1/models/process-pose` (el b64 en el body es cosa del contrato viejo
 *     de n8n); la fuente se borra best-effort al terminar. Flag OFF → webhook
 *     n8n `procesar_pose_subida` byte-idéntico (b64 en el body).
 *     El error de cuota ya no puede pasar por el atlas (es gratis), pero el
 *     path n8n del flag OFF todavía lo devuelve como HTTP 200 +
 *     `{response:'error', error_type:'quota', error_message:'Sin imágenes
 *     suficientes'}` — por eso el check compartido de abajo se queda.
 *  2. El front guarda esa imagen generada: la sube al bucket `modelos`
 *     (`<id_company>/poses_custom/<uuid>.png`) e inserta la fila en
 *     `poses_personalizadas`.
 * El caller refetchea `fetchMyCustomPoses` al terminar.
 */
export async function uploadCustomPose(opts: {
  userId: string;
  idCompany: string;
  file: File;
  nombre?: string;
  /** Cantidad de personas de la pose. Sale del contexto (cantidad de
   *  modelos seleccionados). Default 1. El n8n genera ese número de muñecos. */
  cantidadPersonas?: number;
}): Promise<void> {
  // Normalizar formato igual que el resto de los uploads de V2: las fotos de
  // iPhone (.heic/.avif) viajaban crudas y el n8n / visor no las procesa.
  let file: File;
  try {
    file = await normalizeImageForUpload(opts.file);
  } catch (convErr) {
    // Un formato bloqueado (RAW/TIFF) ya trae su propio mensaje: re-lanzarlo tal
    // cual. El genérico queda solo para fallos reales de conversión (HEIC/AVIF).
    if (convErr instanceof UnsupportedImageFormatError) throw convErr;
    console.error('[posesCustomApi] conversión de formato falló', convErr);
    throw new Error(
      'No pudimos convertir la imagen (HEIC/AVIF). Probá con una imagen en formato png o jpg.',
    );
  }
  // 1. Generar ──────────────────────────────────────────────────────
  let data: {
    response?: string;
    image_pose?: string;
    error_message?: string;
  } | null;
  if (USE_ATLAS_BACKEND) {
    // Subir la foto fuente al bucket (el worker la baja por key). El path
    // DEBE empezar con `<id_company>/` por la policy del bucket `modelos`.
    const srcExt = file.type === 'image/png' ? 'png' : 'jpg';
    const srcPath = `${opts.idCompany}/poses_custom/src_${crypto.randomUUID()}.${srcExt}`;
    const { error: srcErr } = await supabase.storage
      .from(BUCKET)
      .upload(srcPath, file, {
        contentType: file.type || 'image/jpeg',
        upsert: false,
      });
    if (srcErr) throw srcErr;
    const res = await atlasBackendFetch('/v1/models/process-pose', {
      imagen_key: srcPath,
      cantidad_personas: opts.cantidadPersonas ?? 1,
      // El zod del atlas rechaza null — mandar `nombre` solo si existe.
      ...(opts.nombre ? { nombre: opts.nombre } : {}),
    });
    if (!res.ok) {
      const t = await res.text().catch(() => '');
      let msg = t;
      try {
        msg = (JSON.parse(t) as { error_message?: string })?.error_message || t;
      } catch { /* body no-JSON */ }
      throw new Error(msg || `Error ${res.status} procesando la pose`);
    }
    data = await res.json().catch(() => null);
    // La fuente ya no hace falta (el job terminó) — limpieza best-effort. En
    // error/timeout NO se borra: el worker podría seguir usándola.
    void supabase.storage.from(BUCKET).remove([srcPath]);
  } else {
    const imagen = await fileToBase64(file);
    const res = await fetch(PROCESAR_POSE_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: GENERATE_AUTH_TOKEN,
      },
      body: JSON.stringify({
        user_id: opts.userId,
        imagen,
        nombre: opts.nombre ?? null,
        cantidad_personas: opts.cantidadPersonas ?? 1,
      }),
    });
    if (!res.ok) {
      const t = await res.text().catch(() => '');
      throw new Error(t || `Error ${res.status} procesando la pose`);
    }
    data = await res.json().catch(() => null);
  }
  if (!data || data.response !== 'success' || typeof data.image_pose !== 'string') {
    throw new Error(
      (data && data.error_message) || 'Respuesta inesperada del servidor',
    );
  }
  // 2. Guardar (storage + fila) ─────────────────────────────────────
  const blob = b64ToBlob(stripB64Prefix(data.image_pose), 'image/png');
  const filename = `${crypto.randomUUID()}.png`;
  const path = `${opts.idCompany}/poses_custom/${filename}`;
  const { error: upErr } = await supabase.storage
    .from(BUCKET)
    .upload(path, blob, { contentType: 'image/png', upsert: false });
  if (upErr) throw upErr;
  // Nombre genérico numerado, guardado en la fila. Se cuenta ANTES de insertar,
  // así la primera queda "Pose propia 1". Si se borra una del medio quedan
  // huecos (…1, 3, 4) y está bien: el número no se recicla, así el nombre de
  // una pose no cambia por detrás mientras el user la sigue usando.
  let nombre = opts.nombre?.trim() || '';
  if (!nombre) {
    const { count } = await supabase
      .from('poses_personalizadas')
      .select('id', { count: 'exact', head: true })
      .eq('id_company', opts.idCompany);
    nombre = nombrePosePropia((count ?? 0) + 1);
  }
  const { error: insErr } = await supabase
    .from('poses_personalizadas')
    .insert({
      id_company: opts.idCompany,
      nombre,
      filename,
      cantidad_personas: opts.cantidadPersonas ?? 1,
    });
  if (insErr) throw insErr;
}

/** Borra una pose propia: remueve la imagen del storage + la fila (RLS DELETE
 *  acotada por empresa permite el borrado de la fila propia). */
export async function deleteCustomPose(opts: {
  id: string;
  idCompany: string;
  filename: string;
}): Promise<void> {
  const path = `${opts.idCompany}/poses_custom/${opts.filename}`;
  await supabase.storage.from(BUCKET).remove([path]); // best-effort
  const { error } = await supabase
    .from('poses_personalizadas')
    .delete()
    .eq('id', opts.id);
  if (error) throw error;
}
